import { Button } from 'primereact/button';
import { useEffect, useState } from 'react';

import { useDeleteConditionMutation } from '@/components/shared/api/mutations/conditions';
import { Dialog } from '@/components/shared/components/Dialog';
import {
    MentionTextarea,
    useMentionSuggestions,
} from '@/components/shared/components/MentionTextarea';

import {
    ErrorText,
    FormActions,
    FormLabel,
    HelpText,
    ModalContainer,
    ModalField,
    ModalSubtitle,
    ReadonlyConditionTitle,
    RequiredAsterisk,
} from './ApplicantConditions.StyledComponents';
import { Condition } from './ApplicantConditions.types';

interface Props {
    applicantId: string;
    condition: Condition | null;
    visible: boolean;
    onClose: () => void;
}

export const DeleteConditionDialog = ({ applicantId, condition, visible, onClose }: Props) => {
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);

    const mentionSuggestions = useMentionSuggestions(applicantId);

    const { mutate: deleteCondition, isPending } = useDeleteConditionMutation();

    useEffect(() => {
        if (visible) {
            setNote('');
            setError(null);
        }
    }, [visible, condition?.id]);

    const handleClose = () => {
        onClose();
    };

    const handleDelete = () => {
        if (!condition) return;
        const trimmed = note.trim();
        if (!trimmed) {
            setError('A deletion note is required.');
            return;
        }
        deleteCondition(
            { applicantId, conditionId: condition.id, note: trimmed },
            {
                onSuccess: handleClose,
                onError: () => setError('Failed to delete condition. Please try again.'),
            },
        );
    };

    return (
        <Dialog
            id="delete-condition-modal"
            header="Delete Task/Condition"
            visible={visible}
            onHide={handleClose}
            dismissableMask={false}
            data-testid="delete-condition-dialog"
        >
            <ModalContainer>
                <ModalSubtitle>
                    Are you sure you want to delete this task/condition?{' '}
                    <strong style={{ textDecoration: 'underline' }}>This cannot be undone.</strong>
                </ModalSubtitle>
                <ReadonlyConditionTitle data-testid="delete-condition-description">
                    {condition?.description ?? ''}
                </ReadonlyConditionTitle>

                <ModalField>
                    <FormLabel htmlFor="delete-condition-note">
                        Deletion note<RequiredAsterisk>*</RequiredAsterisk>
                    </FormLabel>
                    <MentionTextarea
                        id="delete-condition-note"
                        data-testid="delete-condition-note"
                        placeholder="Explain why this task/condition is being deleted…"
                        value={note}
                        onChange={(val) => {
                            setNote(val);
                            if (error) setError(null);
                        }}
                        hasError={Boolean(error)}
                        spellCheck
                        suggestions={mentionSuggestions}
                    />
                    {error ? (
                        <ErrorText>{error}</ErrorText>
                    ) : (
                        <HelpText>A deletion note is required — Explain why this task/condition is being deleted.</HelpText>
                    )}
                </ModalField>

                <FormActions style={{ justifyContent: 'flex-end' }}>
                    <Button
                        label="Cancel"
                        severity="secondary"
                        outlined
                        onClick={handleClose}
                        disabled={isPending}
                    />
                    <Button
                        label="Delete Task/Condition"
                        severity="danger"
                        onClick={handleDelete}
                        loading={isPending}
                        disabled={isPending || !condition}
                        data-testid="delete-condition-confirm"
                    />
                </FormActions>
            </ModalContainer>
        </Dialog>
    );
};
